import React, { useState } from 'react';

import { Box } from '@rover/kibble/core';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import { FeeExplanationModal } from '../../../../search/SearchPage/components/Modals/FeeExplanationModal';
import { PtCondensedBanner } from '../../../../search/SearchPage/components/PtCondensedBanner';
import { useRevampBrightHorizonsBanner } from '../../hooks/useRevampBrightHorizonsBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { SitterPriceBanner } from '../SitterPriceBanner';

import { CTAPropsCommon } from './RevampServicesCta';

type Props = Omit<CTAPropsCommon, 'locale'> & {
  /**
   * Which container the banner bleeds out of: the desktop CTA card (rounded
   * bottom corners) or the mobile bottom sheet (flush with the sheet's edge).
   */
  variant?: 'card' | 'sheet';
};

const RevampCtaPtBanner = ({
  priceTransparencyLevel,
  petType,
  petCount,
  bookingFeePercentage,
  variant = 'card',
}: Props): JSX.Element | null => {
  const { serviceType, selectedService } = useRevampCta();
  const { isPtFull, isPtCondensed, countryConfigUrls, petTypeServices } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );
  const { isShowingBhBanner } = useRevampBrightHorizonsBanner(petType, petCount);
  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);

  if (!isPtCondensed && !isPtFull) return null;
  // A Bright Horizons user reads their eligibility banner instead of the full
  // price breakdown, the way the other CTA layouts hand it over.
  if (isPtFull && !isPtCondensed && isShowingBhBanner) return null;

  return (
    // The banner spans its whole container, so it cancels the container's
    // horizontal and bottom padding. The card also re-clips the bottom corners to
    // its own radius; the sheet's bottom edge is square.
    <Box
      data-testid="revamp-cta-pt-banner"
      mt="6x"
      mx="-4x"
      mb={variant === 'card' ? '-8x' : '-6x'}
      sx={
        variant === 'card'
          ? {
              overflow: 'hidden',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
            }
          : undefined
      }
    >
      {isPtCondensed ? (
        <PtCondensedBanner
          priceTransparencyLevel={priceTransparencyLevel}
          bookingFeePercentage={bookingFeePercentage}
          onClick={() => setIsFeeExplanationModalOpen(true)}
        />
      ) : (
        <SitterPriceBanner
          petCounts={petCount}
          petType={petType}
          serviceType={serviceType}
          countryConfigUrls={countryConfigUrls}
          bookingFeePercentage={bookingFeePercentage}
          services={petTypeServices}
          version="revamp"
        />
      )}
      <FeeExplanationModal
        isOpen={isFeeExplanationModalOpen}
        onClose={() => setIsFeeExplanationModalOpen(false)}
        priceTransparencyLevel={priceTransparencyLevel || PriceTransparencyLevels.lite_condensed}
        bookingPercentageFee={bookingFeePercentage}
      />
    </Box>
  );
};

export default RevampCtaPtBanner;
